import React, { useState, useEffect } from 'react';
import { loadTelegramConfig, saveTelegramConfig } from '../../utils/telegram';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faCheck, faPaperPlane, faTimes } from '@fortawesome/free-solid-svg-icons';
import './TelegramSettings.css';

interface TelegramSettingsProps {
  onClose: () => void;
}

const TelegramSettings: React.FC<TelegramSettingsProps> = ({ onClose }) => {
  const [botToken, setBotToken] = useState('');
  const [chatId, setChatId] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [testStatus, setTestStatus] = useState<{ message: string; success: boolean } | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isInSettingsPage, setIsInSettingsPage] = useState(true);

  useEffect(() => {
    // Load existing configuration
    const config = loadTelegramConfig();
    setBotToken(config.botToken || '');
    setChatId(config.chatId || '');
    setEnabled(config.enabled || false);
    
    // Detect if component is being used in settings page
    setIsInSettingsPage(onClose === (() => {}));
  }, [onClose]);

  const handleSave = () => {
    setIsSaving(true);
    
    // Save configuration
    saveTelegramConfig({
      botToken,
      chatId,
      enabled
    });
    
    // Show success indicator briefly
    setTimeout(() => {
      setIsSaving(false);
      onClose();
    }, 1000);
  };

  const handleTestConnection = async () => {
    try {
      setTestStatus({ message: 'Testing connection...', success: false });
      
      const response = await fetch(`https://api.telegram.org/bot${botToken}/sendMessage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          chat_id: chatId,
          text: 'This is a test message from your receipt system. If you can see this, your Telegram integration is working correctly!'
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setTestStatus({ message: 'Test successful! Check your Telegram.', success: true });
      } else {
        setTestStatus({ message: `Test failed: ${data.description || 'Unknown error'}`, success: false });
      }
    } catch (error) {
      setTestStatus({ 
        message: `Connection error: ${error instanceof Error ? error.message : 'Unknown error'}`, 
        success: false 
      });
    }
  };

  return (
    <div className={`telegram-settings ${isInSettingsPage ? 'in-settings-page' : ''}`}>
      {!isInSettingsPage && (
        <div className="telegram-settings-header">
          <h3>Telegram Integration Settings</h3>
          <button className="close-button" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}
      
      <div className="telegram-settings-content">
        <div className="form-group">
          <label htmlFor="telegram-enabled">
            <input
              type="checkbox"
              id="telegram-enabled"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            Enable Telegram Receipt Notifications
          </label>
        </div>
        
        <div className="form-group">
          <label htmlFor="bot-token">Bot Token</label>
          <input
            type="text"
            id="bot-token"
            value={botToken}
            onChange={(e) => setBotToken(e.target.value)}
            placeholder="Enter your Telegram bot token"
            disabled={!enabled}
          />
          <small>Create a bot via BotFather and paste the token here</small>
        </div>
        
        <div className="form-group">
          <label htmlFor="chat-id">Chat ID</label>
          <input
            type="text"
            id="chat-id"
            value={chatId}
            onChange={(e) => setChatId(e.target.value)}
            placeholder="Enter the chat ID to send receipts to"
            disabled={!enabled}
          />
          <small>This can be your personal chat ID, a group, or a channel</small>
        </div>
        
        {testStatus && (
          <div className={`test-status ${testStatus.success ? 'success' : 'error'}`}>
            <FontAwesomeIcon icon={testStatus.success ? faCheck : faTimes} />
            <span>{testStatus.message}</span>
          </div>
        )}
        
        <div className="button-group">
          <button 
            className="test-button" 
            onClick={handleTestConnection}
            disabled={!enabled || !botToken || !chatId}
          >
            <FontAwesomeIcon icon={faPaperPlane} /> Test Connection
          </button>
          
          <button 
            className="save-button" 
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <FontAwesomeIcon icon={faCheck} /> Saved!
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} /> Save Settings
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TelegramSettings;
